import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, Mail, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
        toast.success('Reset link sent if email exists');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Activity className="w-7 h-7 text-white" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight uppercase">
            SportsAnalysisAB
          </span>
        </Link>

        {/* Card */}
        <div className="bg-card border border-border rounded-sm p-8">
          {!sent ? (
            <>
              <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-center mb-2">
                Reset Password
              </h1>
              <p className="text-sm text-muted-foreground text-center mb-8">
                Enter your email and we'll send you a reset link
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 rounded-sm"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">
                Check Your Email
              </h2>
              <p className="text-muted-foreground mb-6">
                If an account exists with {email}, we've sent a password reset link.
              </p>
              <Button 
                variant="outline"
                className="rounded-sm"
                onClick={() => setSent(false)}
              >
                Try Another Email
              </Button>
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center mt-6">
            Remember your password?{' '}
            <Link to="/login" className="text-foreground hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
